import {
  BrainCircuit,
  Radar,
  ShieldCheck,
  BellRing,
  Smartphone,
  BarChart3,
  Siren,
} from "lucide-react";
import Reveal from "./Reveal";

const features = [
  {
    icon: BrainCircuit,
    title: "AI-Powered Leak Detection",
    body: "Learns your home's unique plumbing behaviour to spot anomalies with precision.",
    span: "md:col-span-2 md:row-span-2",
    big: true,
  },
  {
    icon: Radar,
    title: "24/7 Real-Time Monitoring",
    body: "Your entire water system, watched around the clock.",
  },
  {
    icon: Siren,
    title: "Automatic Emergency Shut-Off",
    body: "Minimises water damage the instant a major leak strikes.",
  },
  {
    icon: BellRing,
    title: "Instant Smartphone Alerts",
    body: "Know the moment something's wrong — wherever you are.",
  },
  {
    icon: Smartphone,
    title: "Remote Water Control",
    body: "Shut off and manage your supply from the app.",
  },
  {
    icon: BarChart3,
    title: "Detailed Usage Analytics",
    body: "Identify waste and improve efficiency over time.",
  },
  {
    icon: ShieldCheck,
    title: "Early Warning Technology",
    body: "Catches small issues before they become major problems.",
  },
];

export default function Features() {
  return (
    <section
      id="features"
      data-testid="features-section"
      className="relative bg-[#050505] py-28 lg:py-40"
    >
      <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8">
          <div className="max-w-2xl">
            <Reveal>
              <span className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#169ED9]">
                / Smart features
              </span>
            </Reveal>
            <Reveal delay={0.05}>
              <h2 className="mt-6 font-heading font-extrabold tracking-tighter text-4xl sm:text-5xl lg:text-6xl leading-[0.95]">
                Smarter Plumbing, Greater Protection.
              </h2>
            </Reveal>
          </div>
          <Reveal delay={0.1}>
            <p className="max-w-sm text-white/50 leading-relaxed">
              Whether you&apos;re at home, at work, or away on holiday — continuous
              protection and peace of mind, 24 hours a day.
            </p>
          </Reveal>
        </div>

        <div className="mt-16 grid grid-cols-1 md:grid-cols-4 gap-4 auto-rows-[minmax(180px,auto)]">
          {features.map((f, i) => {
            const Icon = f.icon;
            return (
              <Reveal key={f.title} delay={(i % 4) * 0.06} className={f.span || ""}>
                <div
                  data-testid={`feature-${i}`}
                  className="group relative h-full overflow-hidden rounded-2xl border border-white/10 bg-[#0a0a0a] p-8 transition-colors duration-500 hover:border-[#169ED9]/40"
                >
                  <div className="pointer-events-none absolute -right-16 -top-16 h-40 w-40 rounded-full bg-[#00E5FF]/0 blur-3xl transition-all duration-500 group-hover:bg-[#00E5FF]/15" />
                  <Icon
                    className={`relative text-[#169ED9] ${f.big ? "h-10 w-10" : "h-7 w-7"}`}
                    strokeWidth={1.4}
                  />
                  <div className={`relative ${f.big ? "mt-10" : "mt-6"}`}>
                    <h3
                      className={`font-heading font-bold tracking-tight ${
                        f.big ? "text-2xl lg:text-3xl" : "text-lg"
                      }`}
                    >
                      {f.title}
                    </h3>
                    <p
                      className={`mt-3 text-white/50 leading-relaxed ${
                        f.big ? "text-base max-w-sm" : "text-sm"
                      }`}
                    >
                      {f.body}
                    </p>
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}
